import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Activity, UserPlus, UserMinus, Trash2, Shield, Edit3, 
  Flag, Clock, Loader2, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ACTION_ICONS = {
  'member_approved': UserPlus,
  'member_removed': UserMinus,
  'member_banned': UserMinus,
  'post_deleted': Trash2,
  'role_changed': Shield,
  'group_updated': Edit3,
  'content_reported': Flag,
  'default': Activity
};

const ACTION_COLORS = {
  'member_approved': 'bg-green-100 text-green-700',
  'member_removed': 'bg-red-100 text-red-700',
  'member_banned': 'bg-red-100 text-red-700',
  'post_deleted': 'bg-orange-100 text-orange-700',
  'role_changed': 'bg-blue-100 text-blue-700',
  'group_updated': 'bg-purple-100 text-purple-700',
  'content_reported': 'bg-amber-100 text-amber-700',
  'default': 'bg-gray-100 text-gray-700'
};

const ActivityLogViewer = ({ groupId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await api.get(`/groups/${groupId}/activity-log`);
      setLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [groupId]);

  const getIcon = (action) => {
    const IconComponent = ACTION_ICONS[action] || ACTION_ICONS.default;
    return IconComponent;
  };

  const getColorClass = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.default;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Activity Log
        </CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fetchLogs(true)}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No activity recorded yet</p>
            <p className="text-sm">Admin actions will appear here</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {logs.map((log) => {
                const IconComponent = getIcon(log.action);
                return (
                  <div 
                    key={log.log_id} 
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className={`p-2 rounded-full ${getColorClass(log.action)}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-semibold">{log.actor_name}</span>
                        {' '}
                        <span className="text-gray-600">{log.action.replace(/_/g, ' ')}</span>
                        {log.target_id && (
                          <span className="text-gray-500"> • {log.target_type}</span>
                        )}
                      </p>
                      {log.details && (
                        <p className="text-xs text-gray-500 mt-1">{log.details}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(log.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityLogViewer;
